import s from "./HeaderDropdown.module.scss";
import { Link } from "@/components/shared/Link";
import { SpriteIcon } from "@/components/UI/SpriteIcon";
import IconArrowDown from "public/img/icons/arrow-down.svg";
import IconArrowRight from "public/img/icons/arrow-right.svg";
import { iconDict } from "@/components/pages/tools/ToolIcons";
import { Container } from "@/components/layout/Container";
import { MouseEvent as ReactMouseEvent, useCallback, useEffect, useRef, useState } from "react";
import cn from "classnames";
import { usePathname } from "next/navigation";

type HeaderDropdownSubItem = {
  text: string;
  href?: string;
};

export type HeaderDropdownItem = {
  icon?: string;
  text: string;
  href?: string;
  items?: HeaderDropdownSubItem[];
};

type HeaderDropdownProps = {
  items: HeaderDropdownItem[];
  title: string;
  currentOpenedDropdown?: string;
  onOpen: (value: string) => void;
  onOpenItem: (value: string | null) => void;
  onOpenSubItem: (value: string | null) => void;
};

export const HeaderDropdown = ({
  title,
  items,
  currentOpenedDropdown,
  onOpen,
  onOpenSubItem,
  onOpenItem,
}: HeaderDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const asPath = usePathname();

  const activeIndex = items.findIndex(
    ({ href, items }) =>
      asPath === String(href) ||
      String(href) === "/" + asPath.split("/")[1] ||
      items?.find(({ href }) => asPath === String(href) || String(href) === "/" + asPath.split("/")[1])
  );

  // handle initially not selected case
  const [currentSelectedIndex, setCurrentSelectedIndex] = useState(activeIndex > -1 ? activeIndex : 0);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const openMenu = () => {
    setOpen(true);
    onOpen(title);
    onOpenItem(title);
  };

  const closeMenu = useCallback(() => {
    setOpen(false);
    setClosing(true);
    setCurrentSelectedIndex(activeIndex > -1 ? activeIndex : 0);
    setTimeout(() => {
      setClosing(false);
    }, 0);
  }, [activeIndex]);

  const watchMouseLeave = useCallback(() => {
    if (window.matchMedia("(max-width: 1200px)").matches) {
      return;
    }
    closeMenu();
  }, [closeMenu]);

  useEffect(() => {
    if (!wrapperRef.current) return;

    const wrapper = wrapperRef.current;

    wrapper.addEventListener("mouseleave", watchMouseLeave);

    return () => {
      wrapper.removeEventListener("mouseleave", watchMouseLeave);
    };
  }, [watchMouseLeave]);

  useEffect(() => {
    if (currentOpenedDropdown && title !== currentOpenedDropdown) {
      closeMenu();
    }
  }, [closeMenu, currentOpenedDropdown, title]);

  const hasNestedItems = items.find(({ items }) => !!items?.length);

  const handleDropdownItemClick = (event: ReactMouseEvent, text: string) => {
    if (window.matchMedia("(max-width: 1200px)").matches) {
      event.preventDefault();

      if (window.matchMedia("(max-width: 576px)").matches) {
        onOpenItem(text);
      }
      return;
    }
    closeMenu();
  };

  const handleDropdownSubItemClick = (event: ReactMouseEvent, text: string) => {
    if (window.matchMedia("(max-width: 576px)").matches) {
      onOpenSubItem(text);
      return;
    }

    closeMenu();
  };

  useEffect(() => {
    if (!wrapperRef.current || !hasNestedItems) return;
    const items: HTMLLIElement[] = Array.from(wrapperRef.current.querySelectorAll(`.${s.menu} > li`));

    const watchMouseEnter = (event: MouseEvent) => {
      setCurrentSelectedIndex(items.findIndex((item) => item === event.target));
    };

    const watchMouseLeave = (event: MouseEvent) => {
      setCurrentSelectedIndex(items.findIndex((item) => item === event.target));
    };
    //
    items.forEach((item) => {
      item.addEventListener("mouseenter", watchMouseEnter);
      item.addEventListener("mouseleave", watchMouseLeave);
    });

    return () => {
      items.forEach((item) => {
        item.removeEventListener("mouseenter", watchMouseEnter);
        item.removeEventListener("mouseleave", watchMouseLeave);
      });
    };
  }, [hasNestedItems]);

  return (
    <div
      className={cn(s.wrapper, {
        [s.open]: open,
        [s.closing]: closing,
        [s.noNested]: !hasNestedItems,
      })}
    >
      <button type="button" className={s.button} onClick={() => (open ? closeMenu() : openMenu())}>
        {title}
        <SpriteIcon icon={IconArrowDown} />
      </button>
      <div className={s.menuWrap} ref={wrapperRef}>
        <Container>
          <ul className={s.menu}>
            {items.map(({ icon, text, href, items }, index) => (
              <li
                key={index}
                className={cn(s.menuItem, {
                  [s.itemActive]:
                    asPath === String(href) ||
                    String(href) === "/" + asPath.split("/")[1] ||
                    index === currentSelectedIndex,
                  [s.itemHovered]: index === currentSelectedIndex,
                })}
              >
                <Link
                  href={String(href)}
                  className={s.itemTitle}
                  onClick={(event) => handleDropdownItemClick(event, text)}
                >
                  <span>
                    {icon && <SpriteIcon icon={iconDict[icon]} className={s.icon} />}
                    {text}
                  </span>
                  {hasNestedItems && <SpriteIcon icon={IconArrowRight} className={s.arrow} />}
                </Link>
                <div className={s.subitemsWrap}>
                  {!!items?.length && (
                    <ul className={s.subitems}>
                      {items.map(({ text, href }, key) => (
                        <li key={key}>
                          <Link
                            href={String(href)}
                            className={cn({
                              [s.linkActive]: asPath === href,
                            })}
                            onClick={(event) => {
                              handleDropdownSubItemClick(event, text);
                            }}
                          >
                            {text}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Container>
      </div>
    </div>
  );
};
